import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * Custom dropdown: a pill button with a rotating chevron and a popover menu
 * that marks the selected option with a check. Replaces the native <select>
 * so the menu can be styled (selected item in brand color, hover states).
 */
export function Select({
  value,
  onChange,
  options,
  placeholder,
  className,
  disabled,
}: SelectProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-lg border bg-white px-3 py-1.5 text-sm transition-colors',
          open
            ? 'border-brand-500 ring-1 ring-brand-500'
            : 'border-slate-300 hover:border-slate-400',
          'disabled:cursor-not-allowed disabled:opacity-50',
        )}
      >
        <span className={cn('truncate', selected ? 'text-slate-900' : 'text-slate-400')}>
          {selected ? selected.label : (placeholder ?? 'Válassz…')}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-slate-400 transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && (
        <div className="absolute left-0 z-30 mt-1 max-h-64 min-w-full overflow-auto rounded-lg border border-slate-200 bg-white p-1 shadow-lg">
          {options.map((o) => {
            const isSelected = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-slate-50',
                  isSelected ? 'font-medium text-brand-700' : 'text-slate-700',
                )}
              >
                <Check
                  className={cn('h-4 w-4 shrink-0', isSelected ? 'text-brand-600' : 'invisible')}
                />
                <span className="truncate">{o.label}</span>
              </button>
            );
          })}
          {options.length === 0 && (
            <div className="px-2 py-1.5 text-sm text-slate-400">Nincs opció</div>
          )}
        </div>
      )}
    </div>
  );
}
